/**
 * Evidence Upload Slots Component
 * 
 * Dynamic upload slots for client portal, generated from Evidence Checklist.
 * Features:
 * - Priority-ordered slots (Critical → High → Medium → Low)
 * - Color-coded badges
 * - Soft validation warnings for doc type mismatch
 * - Progress tracking
 * - "Other Documents" catch-all
 */

import React, { useState, useCallback, useEffect } from 'react';
import { 
  Upload, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Image, 
  File,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import api from '../utils/api';

interface UploadSlot {
  id: string;
  document_name: string;
  reason: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  priority_color: string;
  priority_order: number;
  expected_doc_type: string | null;
  status: 'pending' | 'uploaded' | 'obtained';
  uploaded_file: {
    filename: string;
    status: string;
    detected_type: string | null;
  } | null;
  financial_impact: number | null;
}

interface EvidenceUploadSlotsProps {
  reportId: string;
  claimId?: string;
  draftId?: string;
  clientName?: string;
  onUploadComplete?: () => void;
}

interface SlotFile {
  file: File;
  uploading: boolean;
  progress: number;
  error: string | null;
  warning: string | null;
  uploadId: string | null;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export default function EvidenceUploadSlots({
  reportId,
  claimId,
  draftId,
  clientName,
  onUploadComplete
}: EvidenceUploadSlotsProps) {
  const [slots, setSlots] = useState<UploadSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotFiles, setSlotFiles] = useState<Record<string, SlotFile>>({});
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [otherUploading, setOtherUploading] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState({ total: 0, uploaded: 0, progress_percent: 0 });

  // Fetch upload slots
  useEffect(() => {
    fetchSlots();
  }, [reportId]);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const result = await api.get(`/api/v1/evidence/portal/${reportId}/upload-slots`);
      setSlots(result.slots || []);
      setStats(result.stats || { total: 0, uploaded: 0, progress_percent: 0 });
      
      // Auto-expand critical items
      const criticalIds = (result.slots || [])
        .filter((s: UploadSlot) => s.priority === 'CRITICAL')
        .map((s: UploadSlot) => s.id);
      setExpandedSlots(new Set(criticalIds));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSlot = (slotId: string) => {
    setExpandedSlots(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }
      return next;
    });
  };

  const handleSlotFileSelect = async (slotId: string, file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { file, uploading: false, progress: 0, error: 'File too large (max 100MB)', warning: null, uploadId: null }
      }));
      return;
    }

    // Set file in state
    setSlotFiles(prev => ({
      ...prev,
      [slotId]: { file, uploading: true, progress: 10, error: null, warning: null, uploadId: null }
    }));

    // Upload the file
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('claim_id', claimId || reportId);
      formData.append('draft_id', draftId || '');
      formData.append('uploader_type', 'client');
      formData.append('client_name', clientName || '');
      formData.append('evidence_item_id', slotId);

      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { ...prev[slotId], progress: 30 }
      }));

      const result = await api.uploadFile('/api/v1/upload-savefirst/unified', formData);
      
      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { 
          ...prev[slotId], 
          uploading: false, 
          progress: 100, 
          uploadId: result.upload_id,
          error: null
        }
      }));

      // Refresh slots to get updated status
      setTimeout(() => {
        fetchSlots();
        onUploadComplete?.();
      }, 1000);

    } catch (err: any) {
      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { ...prev[slotId], uploading: false, progress: 0, error: err.message }
      }));
    }
  };

  const handleOtherFilesSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter(f => f.size <= MAX_FILE_SIZE);
    setOtherFiles(prev => [...prev, ...newFiles]);
  };

  const uploadOtherFiles = async () => {
    if (otherFiles.length === 0) return;
    
    setOtherUploading(true);
    try {
      for (const file of otherFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('claim_id', claimId || reportId);
        formData.append('draft_id', draftId || '');
        formData.append('uploader_type', 'client');
        formData.append('client_name', clientName || '');

        await api.uploadFile('/api/v1/upload-savefirst/unified', formData);
      }
      
      setOtherFiles([]);
      onUploadComplete?.();
    } catch (err: any) {
      console.error('Upload failed:', err);
    } finally {
      setOtherUploading(false);
    }
  };

  const removeOtherFile = (index: number) => {
    setOtherFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return '🔴';
      case 'HIGH': return '🟠';
      case 'MEDIUM': return '🟡';
      case 'LOW': return '🟢';
      default: return '⚪';
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
      return <Image className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading document requirements...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="p-6 bg-green-50 border border-green-200 rounded-lg text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-800">All Documents Received</h3>
        <p className="text-green-600">No additional documents are needed at this time.</p>
      </div>
    );
  }

  // Group slots by priority
  const groupedSlots = {
    CRITICAL: slots.filter(s => s.priority === 'CRITICAL'),
    HIGH: slots.filter(s => s.priority === 'HIGH'),
    MEDIUM: slots.filter(s => s.priority === 'MEDIUM'),
    LOW: slots.filter(s => s.priority === 'LOW')
  };

  return (
    <div className="space-y-6" data-testid="evidence-upload-slots">
      {/* Progress Header */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Documents Needed for Your Claim</h2>
          <span className="text-sm text-gray-500">
            {stats.uploaded} of {stats.total} uploaded
          </span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
            style={{ width: `${stats.progress_percent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Upload the documents below to help us process your claim faster
        </p>
      </div>

      {/* Priority Sections */}
      {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(priority => {
        const prioritySlots = groupedSlots[priority];
        if (prioritySlots.length === 0) return null;

        const priorityLabels = {
          CRITICAL: { label: 'Critical', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
          HIGH: { label: 'High Priority', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
          MEDIUM: { label: 'Medium Priority', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' },
          LOW: { label: 'Low Priority', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' }
        };

        const style = priorityLabels[priority];

        return (
          <div key={priority} className="space-y-3">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${style.bg}`}>
              <span>{getPriorityIcon(priority)}</span>
              <span className={`font-semibold ${style.text}`}>{style.label}</span>
              <span className="text-sm text-gray-500">({prioritySlots.length} items)</span>
            </div>

            {prioritySlots.map(slot => {
              const slotFile = slotFiles[slot.id];
              const isExpanded = expandedSlots.has(slot.id);
              const isCompleted = slot.status === 'obtained' || slot.status === 'uploaded';

              return (
                <div 
                  key={slot.id}
                  className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
                    isCompleted ? 'border-green-300 bg-green-50' : style.border
                  }`}
                  data-testid={`upload-slot-${slot.id}`}
                >
                  {/* Slot Header */}
                  <button
                    onClick={() => toggleSlot(slot.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <FileText className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="text-left">
                        <span className="font-medium text-gray-900">{slot.document_name}</span>
                        {slot.uploaded_file && (
                          <span className="ml-2 text-sm text-green-600">
                            ✓ {slot.uploaded_file.filename}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span 
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{ backgroundColor: slot.priority_color + '20', color: slot.priority_color }}
                      >
                        {slot.priority}
                      </span>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      {/* Reason */}
                      <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 rounded-lg">
                        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-700">{slot.reason}</p>
                      </div>

                      {/* Upload Area */}
                      {!isCompleted && (
                        <div className="mt-4">
                          {slotFile?.uploading ? (
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="text-sm text-gray-600">Uploading {slotFile.file.name}...</span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 transition-all"
                                  style={{ width: `${slotFile.progress}%` }}
                                />
                              </div>
                            </div>
                          ) : slotFile?.error ? (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-center gap-2 text-red-700">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-sm">{slotFile.error}</span>
                              </div>
                              <label className="mt-2 inline-block cursor-pointer text-sm text-blue-600 hover:underline">
                                Try again
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => e.target.files?.[0] && handleSlotFileSelect(slot.id, e.target.files[0])}
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="block cursor-pointer">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-600">
                                  Drop your <strong>{slot.document_name}</strong> here
                                </p>
                                <p className="text-xs text-gray-400 mt-1">or click to browse</p>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleSlotFileSelect(slot.id, e.target.files[0])}
                                data-testid={`file-input-${slot.id}`}
                              />
                            </label>
                          )}

                          {/* Validation Warning */}
                          {slotFile?.warning && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                                <div>
                                  <p className="text-sm text-amber-700">{slotFile.warning}</p>
                                  <button className="mt-2 text-sm text-amber-800 font-medium hover:underline">
                                    Upload anyway
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Already Uploaded */}
                      {isCompleted && slot.uploaded_file && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="text-sm font-medium text-green-800">{slot.uploaded_file.filename}</p>
                            <p className="text-xs text-green-600">Uploaded successfully</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Other Documents Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-lg">➕</span>
            <span className="font-semibold text-gray-700">Other Documents</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Have additional documents not listed above? Upload them here.</p>
        </div>
        
        <div className="p-4">
          {/* File List */}
          {otherFiles.length > 0 && (
            <div className="mb-4 space-y-2">
              {otherFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {getFileIcon(file.name)}
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-400">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </div>
                  <button
                    onClick={() => removeOtherFile(idx)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
              <button
                onClick={uploadOtherFiles}
                disabled={otherUploading}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {otherUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {otherFiles.length} file{otherFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Drop Zone */}
          <label className="block cursor-pointer">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <File className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Drop additional files here</p>
              <p className="text-xs text-gray-400 mt-1">PDF, Excel, CSV, Images (max 100MB each)</p>
            </div>
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleOtherFilesSelect(e.target.files)}
              data-testid="other-files-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
